// Overhead Allocation — Basis Snapshots (MA-R2, Feb 2026)
//
// Screen 2 of 2. For each Active Allocation Rule, compute a per-period
// snapshot that freezes the numeric basis + normalised weights. Fixed %
// snapshots inherit rule weights verbatim; Revenue / Direct Cost /
// Headcount snapshots read Aggregator Services for the period; Manual
// snapshots require the user to type basis_value per target.
//
// Snapshot lifecycle: Draft → Frozen. Frozen snapshots are the input
// contract for MA-R3's Allocation Run.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import {
  Play, Snowflake, Trash2, X, RefreshCw, Search, History, AlertCircle, Check,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_CHIPS = {
  Draft:       "bg-slate-100 text-slate-700 border-slate-300",
  Frozen:      "bg-sky-50 text-sky-700 border-sky-300",
  Superseded:  "bg-slate-100 text-slate-400 line-through border-slate-300",
};

export default function BasisSnapshotsPage() {
  const [facets, setFacets] = useState(null);
  const [activeRules, setActiveRules] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ rule_id: "", period_key: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [showCompute, setShowCompute] = useState(false);
  const [detail, setDetail] = useState(null);              // snapshot being viewed
  const [auditRows, setAuditRows] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  async function loadInit() {
    try {
      const [f, r] = await Promise.all([
        api.get("/mca/allocation-rules/facets"),
        api.get("/mca/allocation-rules?status=Active&limit=500"),
      ]);
      setFacets(f.data);
      setActiveRules(r.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rules");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.rule_id)    q.set("rule_id", filter.rule_id);
      if (filter.period_key) q.set("period_key", filter.period_key);
      if (filter.status)     q.set("status", filter.status);
      const { data } = await api.get(`/mca/basis-snapshots?${q.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load snapshots");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadInit(); }, []);
  useEffect(() => { load(); }, [filter.rule_id, filter.period_key, filter.status]);

  async function freeze(row) {
    if (!window.confirm(`Freeze snapshot for ${row.rule_code} · ${row.period_key}? Frozen snapshots cannot be edited.`)) return;
    try {
      await api.post(`/mca/basis-snapshots/${row.id}/freeze`);
      toast.success("Snapshot frozen");
      load();
      if (detail?.id === row.id) {
        const { data } = await api.get(`/mca/basis-snapshots/${row.id}`);
        setDetail(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freeze failed");
    }
  }

  async function deleteDraft(row) {
    if (!window.confirm(`Delete Draft snapshot for ${row.rule_code} · ${row.period_key}?`)) return;
    try {
      await api.delete(`/mca/basis-snapshots/${row.id}`);
      toast.success("Snapshot deleted");
      if (detail?.id === row.id) setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function openAudit(row) {
    try {
      const { data } = await api.get(`/mca/basis-snapshots/${row.id}/audit`);
      setAuditRows(data.rows || []);
      setShowAudit(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit fetch failed");
    }
  }

  const canFreeze = (r) => r.status === "Draft" && Math.abs((Number(r.total_weight_percent) || 0) - 100) <= 0.01;

  return (
    <div data-testid="basis-snapshots-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Overhead Allocation · MA-R2"
        title="Basis Snapshots"
        subtitle="Per-period frozen basis + normalised weights. Compute a Draft for one Active rule × one period, review or edit the numbers, then Freeze. Only Frozen snapshots will be consumed by the MA-R3 Allocation Run."
        actions={
          <button data-testid="basis-snapshot-compute-btn"
            onClick={() => setShowCompute(true)}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <RefreshCw size={14} /> Compute Snapshot
          </button>
        }
      />

      {/* Filters */}
      <Card className="mb-4 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[260px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Rule</label>
          <select value={filter.rule_id} onChange={e => setFilter({ ...filter, rule_id: e.target.value })}
            className={inputCls} data-testid="basis-snapshots-rule-filter">
            <option value="">All Active Rules</option>
            {activeRules.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Period</label>
          <input type="month" value={filter.period_key}
            onChange={e => setFilter({ ...filter, period_key: e.target.value })}
            className={inputCls} data-testid="basis-snapshots-period-filter" />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
          <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}
            className={inputCls} data-testid="basis-snapshots-status-filter">
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="Frozen">Frozen</option>
            <option value="Superseded">Superseded</option>
          </select>
        </div>
        <button onClick={() => setFilter({ rule_id: "", period_key: "", status: "" })}
          className="px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
          data-testid="basis-snapshots-clear-filters">Clear</button>
      </Card>

      {/* List */}
      <Card>
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
            {loading ? "Loading…" : `${total} snapshot${total === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="basis-snapshots-table">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <Th>Rule</Th>
                <Th>Period</Th>
                <Th>Basis</Th>
                <Th>Targets</Th>
                <Th className="text-right">Σ Basis</Th>
                <Th className="text-right">Σ Weight</Th>
                <Th>Status</Th>
                <Th>Computed</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="9" className="p-6 text-center text-slate-400 text-sm">
                  No snapshots yet. Compute one for an Active rule × period to begin.
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setDetail(r)}>
                  <Td>
                    <div className="font-mono text-[12px]">{r.rule_code}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-1">{r.rule_name}</div>
                  </Td>
                  <Td mono>{r.period_key}</Td>
                  <Td>{r.basis_type}</Td>
                  <Td>{(r.targets || []).length}</Td>
                  <Td className="text-right font-mono text-[12px]">{fmtNum(r.total_basis_value)}</Td>
                  <Td className="text-right">
                    <span className={Math.abs((Number(r.total_weight_percent) || 0) - 100) <= 0.01
                        ? "font-mono text-emerald-700" : "font-mono text-red-600"}
                      data-testid={`basis-snapshot-total-weight-${r.id}`}>
                      {fmtNum(r.total_weight_percent)}%
                    </span>
                  </Td>
                  <Td>
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${STATUS_CHIPS[r.status] || ""}`}
                      data-testid={`basis-snapshot-status-${r.id}`}>
                      {r.status}
                    </span>
                  </Td>
                  <Td>
                    <div className="text-[11px] text-slate-500">{new Date(r.computed_at).toLocaleDateString()}</div>
                    <div className="text-[10px] text-slate-400">v{r.engine_version}</div>
                  </Td>
                  <Td className="text-right pr-2" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canFreeze(r) && (
                        <button onClick={() => freeze(r)}
                          className="p-1.5 hover:bg-sky-50 text-sky-700" title="Freeze"
                          data-testid={`basis-snapshot-freeze-${r.id}`}><Snowflake size={14} /></button>
                      )}
                      {r.status === "Draft" && (
                        <button onClick={() => deleteDraft(r)}
                          className="p-1.5 hover:bg-red-50 text-red-600" title="Delete Draft"
                          data-testid={`basis-snapshot-delete-${r.id}`}><Trash2 size={14} /></button>
                      )}
                      <button onClick={() => openAudit(r)}
                        className="p-1.5 hover:bg-slate-100 text-slate-500" title="Audit"
                        data-testid={`basis-snapshot-audit-${r.id}`}><History size={14} /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Compute modal */}
      {showCompute && (
        <ComputeModal
          rules={activeRules}
          onClose={() => setShowCompute(false)}
          onDone={(newSnap) => {
            setShowCompute(false);
            load();
            setDetail(newSnap);
          }}
        />
      )}

      {/* Detail drawer */}
      {detail && (
        <SnapshotDetail
          snapshot={detail}
          onClose={() => setDetail(null)}
          onSaved={async () => {
            const { data } = await api.get(`/mca/basis-snapshots/${detail.id}`);
            setDetail(data);
            load();
          }}
          onFreeze={() => freeze(detail)}
        />
      )}

      {/* Audit drawer */}
      {showAudit && (
        <AuditDrawer rows={auditRows} onClose={() => setShowAudit(false)} />
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, className = "", onClick }) => (
  <td onClick={onClick} className={`px-3 py-2.5 ${mono ? "font-mono text-[12px]" : ""} ${className}`}>{children}</td>
);
const fmtNum = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

// ─────────────────────────────────────────────────────────────────────
// Compute Modal
// ─────────────────────────────────────────────────────────────────────
function ComputeModal({ rules, onClose, onDone }) {
  const [ruleId, setRuleId] = useState(rules[0]?.id || "");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!ruleId) { toast.error("Choose a rule."); return; }
    if (!period) { toast.error("Choose a period (YYYY-MM)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mca/basis-snapshots/compute",
        { rule_id: ruleId, period_key: period });
      toast.success("Snapshot computed (Draft)");
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compute failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="basis-snapshot-compute-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Overhead Allocation</div>
            <div className="text-lg font-bold text-slate-800">Compute Basis Snapshot</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {rules.length === 0 ? (
          <div className="text-[12px] text-sky-800 bg-sky-50 border border-sky-200 p-3">
            <div className="flex gap-2">
              <AlertCircle size={12} className="mt-0.5 flex-shrink-0 text-sky-600" />
              <div>
                <div className="font-bold text-sky-900 mb-0.5">No Active rules yet — expected during implementation.</div>
                All existing projects are currently classified as Revenue Projects.
                Once Finance reclassifies the required projects as <b>Internal Cost</b> or
                <b> Corporate Project</b> and activates an Allocation Rule, snapshots can be
                computed here.
              </div>
            </div>
          </div>
        ) : (
          <>
            <Field label="Active Rule *">
              <select value={ruleId} onChange={e => setRuleId(e.target.value)}
                className={inputCls} data-testid="compute-rule">
                {rules.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.code} · {r.name} ({r.basis_type})
                  </option>
                ))}
              </select>
            </Field>
            <div className="mt-4">
              <Field label="Period *">
                <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
                  className={inputCls} data-testid="compute-period" />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={onClose}
                className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
                data-testid="compute-cancel">Cancel</button>
              <button onClick={submit} disabled={busy}
                className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-2"
                data-testid="compute-submit">
                <Play size={12} /> {busy ? "Computing…" : "Compute"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Snapshot Detail Drawer
// ─────────────────────────────────────────────────────────────────────
function SnapshotDetail({ snapshot, onClose, onSaved, onFreeze }) {
  const isDraft = snapshot.status === "Draft";
  const isManual = snapshot.basis_type === "Manual";
  const [rows, setRows] = useState(() => (snapshot.targets || []).map(t => ({ ...t })));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows((snapshot.targets || []).map(t => ({ ...t })));
  }, [snapshot.id, snapshot.status]);

  const totalBasis = useMemo(() => rows.reduce((s, r) => s + (Number(r.basis_value) || 0), 0), [rows]);
  const totalWeight = useMemo(() => rows.reduce((s, r) => s + (Number(r.weight_percent) || 0), 0), [rows]);

  function updRow(i, patch) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function normaliseFromBasis() {
    const sum = rows.reduce((s, r) => s + (Number(r.basis_value) || 0), 0);
    if (sum <= 0) { toast.error("Total basis value must be > 0 to normalise."); return; }
    setRows(rows.map(r => ({ ...r, weight_percent: Number(((Number(r.basis_value) || 0) / sum) * 100).toFixed(4) })));
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/mca/basis-snapshots/${snapshot.id}`, {
        targets: rows.map(r => ({
          target_project_id: r.target_project_id,
          basis_value:       Number(r.basis_value) || 0,
          weight_percent:    Number(r.weight_percent) || 0,
        })),
      });
      toast.success("Draft saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  const canFreezeNow = Math.abs(totalWeight - 100) <= 0.01;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="basis-snapshot-detail" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Basis Snapshot</div>
            <div className="text-lg font-bold text-slate-800">
              {snapshot.rule_code} · {snapshot.period_key} · {snapshot.basis_type}
            </div>
            <div className="text-[11px] text-slate-500">
              {snapshot.rule_name} · Source {snapshot.source_project_code}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <Metric label="Status" value={snapshot.status} />
            <Metric label="Engine ver." value={snapshot.engine_version} />
            <Metric label="Σ Basis" value={fmtNum(totalBasis)} />
            <Metric label="Σ Weight" value={`${fmtNum(totalWeight)}%`}
              tone={canFreezeNow ? "ok" : "warn"} />
          </div>

          {snapshot.notes && (
            <div className="bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-800">
              <AlertCircle size={12} className="inline mr-1" /> {snapshot.notes}
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Targets ({rows.length})</div>
              {isDraft && (
                <div className="flex items-center gap-2">
                  <button onClick={normaliseFromBasis}
                    className="text-[11px] uppercase tracking-wider font-bold text-[#F97316] hover:text-[#EA580C]"
                    data-testid="basis-snapshot-normalise">
                    Normalise weights from basis
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <Th>Target Project</Th>
                    <Th className="text-right">Basis Value</Th>
                    <Th className="text-right">Weight %</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.target_project_id} className="border-t border-slate-100"
                        data-testid={`basis-snapshot-target-row-${i}`}>
                      <td className="px-3 py-2 text-[13px]">
                        <div className="font-mono text-[12px]">{r.target_project_code}</div>
                        <div className="text-[11px] text-slate-500">{r.target_project_name}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.01" value={r.basis_value}
                          disabled={!isDraft}
                          onChange={e => updRow(i, { basis_value: e.target.value })}
                          className={`w-32 text-right px-2 py-1 border border-slate-300 text-[12px] font-mono focus:border-[#F97316] focus:outline-none ${!isDraft ? "bg-slate-50 text-slate-500" : "bg-white"}`}
                          data-testid={`basis-snapshot-target-${i}-value`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.0001" value={r.weight_percent}
                          disabled={!isDraft}
                          onChange={e => updRow(i, { weight_percent: e.target.value })}
                          className={`w-32 text-right px-2 py-1 border border-slate-300 text-[12px] font-mono focus:border-[#F97316] focus:outline-none ${!isDraft ? "bg-slate-50 text-slate-500" : "bg-white"}`}
                          data-testid={`basis-snapshot-target-${i}-weight`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr>
                    <td className="px-3 py-2 text-[11px] uppercase font-bold text-slate-500">Total</td>
                    <td className="px-3 py-2 text-right font-mono text-[12px] font-bold">{fmtNum(totalBasis)}</td>
                    <td className={`px-3 py-2 text-right font-mono text-[12px] font-bold ${canFreezeNow ? "text-emerald-700" : "text-red-600"}`}
                        data-testid="basis-snapshot-detail-total-weight">
                      {fmtNum(totalWeight)}% {canFreezeNow ? "✓" : ""}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Frozen by {snapshot.frozen_by || "—"} at {snapshot.frozen_at || "—"}
          </div>
          <div className="flex items-center gap-2">
            {isDraft && (
              <>
                <button onClick={save} disabled={saving}
                  className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                  data-testid="basis-snapshot-save-draft">
                  {saving ? "Saving…" : "Save Draft"}
                </button>
                <button onClick={onFreeze} disabled={!canFreezeNow}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-2"
                  data-testid="basis-snapshot-freeze-from-detail">
                  <Snowflake size={12} /> Freeze
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditDrawer({ rows, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="basis-snapshot-audit" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="text-lg font-bold text-slate-800">Snapshot Audit Trail</div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-2">
          {rows.length === 0 && <div className="text-slate-400 text-sm text-center py-8">No events yet.</div>}
          {rows.map(r => (
            <div key={r.id} className="border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <div className="font-mono">{new Date(r.timestamp).toLocaleString()}</div>
                <div>{r.user_email} <span className="text-slate-400">· {r.user_role}</span></div>
              </div>
              <div className="mt-1 font-bold text-slate-800 text-sm flex items-center gap-2">
                {r.outcome === "success" ? <Check size={14} className="text-emerald-600" /> : <AlertCircle size={14} className="text-red-600" />}
                {r.event_type}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value, tone = "neutral" }) => (
  <div className={`border p-3 ${tone === "ok" ? "border-emerald-200 bg-emerald-50" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className={`text-lg font-bold ${tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-800"}`}>{value}</div>
  </div>
);

const inputCls = "w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);
